<template>
  <div class="ds-textarea ds-form-container">
    <label
      v-if="label && !$slots.label"
      :for="id"
      data-testid="ds-textarea-label"
    >
      {{ label }}
    </label>
    <slot v-if="!!$slots.label" name="label" />
    <DsSkeleton
      v-if="populating || populatingForm"
      :class="['ds-textarea-skeleton', cssClasses]"
      data-testid="ds-textarea-skeleton"
    />
    <textarea
      v-if="!(populating || populatingForm)"
      :id="id"
      ref="textareaRef"
      v-model="model"
      :name="name"
      :class="[
        'ds-textarea-control',
        'ds-form-control',
        {
          'ds-form-control-invalid': !!error,
          'ds-textarea-placeholder-disabled': disabled,
        },
        cssClasses,
      ]"
      :placeholder="placeholder"
      :rows="rows"
      :minlength="minlength"
      :maxlength="maxlength"
      :disabled="disabled"
      :data-testid="dataTestid"
      @focus="scrollToElement"
    />

    <div class="ds-textarea-footer">
      <ErrorMessage
        :name="name"
        as="p"
        class="ds-form-control-invalid-message"
        data-testid="ds-textarea-validation-message"
      />
      <p
        v-if="showNbCharacters"
        class="ds-textarea-characters"
        data-testid="ds-textarea-characters"
      >
        {{ legendCharacters }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { DsBrowser, DsScroll } from "../../helpers";
import { DsEmitBase, DsPropsBase, useDsInputBase } from "../form/DsInputBase";
import { ErrorMessage } from "vee-validate";
import { useI18n } from "vue-i18n";

import DsSkeleton from "../skeleton/DsSkeleton.vue";

defineOptions({
  name: "DsTextarea",
});

const emit = defineEmits([...DsEmitBase, "focused"]);

const props = defineProps({
  ...DsPropsBase,
  modelValue: {
    type: String,
    default: "",
  },
  cssClasses: {
    type: String,
    default: "",
  },
  minlength: {
    type: String,
    required: false,
  },
  maxlength: {
    type: String,
    default: "2000",
  },
  rows: {
    type: Number,
    default: 3,
  },
  autoResize: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  scrollOnFocus: {
    type: Boolean,
    default: false,
  },
  showNbCharacters: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { model, populatingForm, error } = useDsInputBase<string>(props);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const nbCharacters = computed(() => model.value?.length ?? 0);

const legendCharacters = computed((): string => {
  return t("ds.component.textarea.characters", {
    currentCharacters: nbCharacters.value,
    maxCharacters: Number(props.maxlength),
  });
});

function resize(): void {
  const element = textareaRef.value;
  if (props.autoResize && element) {
    //Always put back the height to auto so the height is computed again.
    element.style.height = "auto";
    //If we have some text, let's update the height of the textarea.
    if (props.modelValue.length > 0) {
      element.style.height = `${element.scrollHeight}px`;
    }
  }
}

function scrollToElement(): void {
  if (DsBrowser.isMobile() && props.scrollOnFocus) {
    DsScroll.to(String(props.id));
  }
}

watch(
  () => props.modelValue,
  () => {
    resize();
  }
);

const focus = async (): Promise<void> => {
  await nextTick();
  (textareaRef.value as HTMLTextAreaElement).focus();
  emit("focused");
};

defineExpose({
  focus,
  scrollToElement,
  nbCharacters,
});
</script>

<style lang="scss">
.ds-textarea {
  &-skeleton {
    @apply w-full h-11;
  }

  &-control {
    @apply text-base block border rounded-lg w-full py-[.5625rem] px-4 resize-none;
  }

  &-control:not(.ds-form-control-invalid):not(:focus) {
    @apply bg-gray-50 border-gray-300;
  }

  &-placeholder-disabled {
    @apply placeholder:text-gray-400 placeholder-opacity-10;
  }

  &-footer {
    @apply flex flex-col items-start sm:flex-row sm:justify-between sm:items-center;
  }

  &-characters {
    @apply sm:flex justify-between items-center mt-2 space-y-2 sm:space-y-0 sm:space-x-2 text-sm font-normal text-gray-500 ml-auto;
  }
}
</style>
